import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtDate, fmtEur } from "../lib/api";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Flame, Plus, Pencil, Trash2, X, Wrench, AlertTriangle, CheckCircle2, Package, Truck, Wallet } from "lucide-react";

const emptyForm = () => ({
  fornitore: "", cliente: "", ubicazione: "", tipo: "", matricola: "", quantita: 1,
  data_installazione: "", ultima_manutenzione: "", prossima_scadenza: "", note: "",
  pagato_partner: false, data_pagamento_partner: "", importo_pagato_partner: "", nota_pagamento_partner: "",
});

function PagamentoBadge({ it }) {
  if (it.pagato_partner) return (
    <span className="inline-flex flex-col text-[11px] font-medium px-2 py-0.5 rounded-lg border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`pagamento-badge-${it.id}`}>
      <span>Pagata{it.importo_pagato_partner ? ` · ${fmtEur(it.importo_pagato_partner)}` : ""}</span>
      {it.data_pagamento_partner && <span className="font-normal text-emerald-600">il {fmtDate(it.data_pagamento_partner)}</span>}
    </span>
  );
  return <span className="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200" data-testid={`pagamento-badge-${it.id}`}>Da pagare</span>;
}

function scadenzaBadge(d) {
  if (!d) return { label: "—", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dt = new Date(d + "T00:00:00");
  const days = Math.round((dt - today) / 86400000);
  if (days < 0) return { label: `Scaduto (${fmtDate(d)})`, cls: "bg-rose-100 text-rose-700 border-rose-200" };
  if (days <= 30) return { label: `Scade ${fmtDate(d)} (${days}gg)`, cls: "bg-amber-100 text-amber-700 border-amber-200" };
  return { label: `Valido · ${fmtDate(d)}`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

export default function Antincendio() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [pagFilter, setPagFilter] = useState("tutte");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/antincendio").then((r) => setItems(r.data)).catch(() => {});
    api.get("/antincendio/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (pagFilter === "pagate") list = list.filter((i) => i.pagato_partner);
    else if (pagFilter === "da_pagare") list = list.filter((i) => !i.pagato_partner);
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((i) => `${i.fornitore || ""} ${i.cliente || ""} ${i.tipo || ""} ${i.matricola || ""} ${i.ubicazione || ""}`.toLowerCase().includes(q));
  }, [items, search, pagFilter]);

  const openNew = () => setDialog({ id: null, form: emptyForm() });
  const openEdit = (it) => setDialog({ id: it.id, form: { ...emptyForm(), ...it } });
  const setF = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));

  const save = async () => {
    const f = dialog.form;
    if (!(f.tipo || "").trim() && !(f.fornitore || "").trim()) {
      toast.error("Indica almeno fornitore o tipo prodotto");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...f, quantita: Number(f.quantita) || 1, importo_pagato_partner: Number(f.importo_pagato_partner) || 0, pagato_partner: !!f.pagato_partner };
      if (dialog.id) await api.put(`/antincendio/${dialog.id}`, payload);
      else await api.post("/antincendio", payload);
      toast.success(dialog.id ? "Prodotto aggiornato" : "Prodotto aggiunto");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare ${it.tipo || it.matricola || "questo prodotto"}?`)) return;
    try { await api.delete(`/antincendio/${it.id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="antincendio-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <span className="inline-flex w-9 h-9 items-center justify-center rounded-xl bg-rose-600 text-white shadow-sm"><Flame size={18} /></span> Antincendio · LC Estintori
          </h1>
          <p className="text-sm text-slate-500 mt-1">Registro estintori e prodotti antincendio per fornitore, con scadenze di manutenzione.</p>
        </div>
        <Button onClick={openNew} className="bg-rose-600 hover:bg-rose-700 text-white shadow-sm" data-testid="new-antincendio-btn"><Plus size={16} className="mr-1.5" /> Nuovo prodotto</Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4 border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="kpi-totale"><div className="text-xs text-slate-600 flex items-center gap-1.5"><Package size={14} /> Prodotti ({summary?.quantita || 0} pz)</div><div className="mt-1 font-display text-xl font-bold text-slate-800">{summary?.totale || 0}</div></Card>
        <Card className="p-4 border-dusty-200 bg-gradient-to-br from-dusty-50 to-white" data-testid="kpi-fornitori"><div className="text-xs text-dusty-700 flex items-center gap-1.5"><Truck size={14} /> Fornitori</div><div className="mt-1 font-display text-xl font-bold text-dusty-800">{summary?.fornitori || 0}</div></Card>
        <Card className="p-4 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="kpi-in-scadenza"><div className="text-xs text-amber-700 flex items-center gap-1.5"><Wrench size={14} /> In scadenza (30gg)</div><div className="mt-1 font-display text-xl font-bold text-amber-800">{summary?.in_scadenza || 0}</div></Card>
        <Card className="p-4 border-rose-200 bg-gradient-to-br from-rose-50 to-white" data-testid="kpi-scadute"><div className="text-xs text-rose-700 flex items-center gap-1.5"><AlertTriangle size={14} /> Manutenzioni scadute</div><div className="mt-1 font-display text-xl font-bold text-rose-800">{summary?.scadute || 0}</div></Card>
      </div>

      {/* Pagamenti partner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="kpi-pagate"><div className="text-xs text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={14} /> Forniture pagate al partner</div><div className="mt-1 font-display text-xl font-bold text-emerald-800">{summary?.pagate || 0}</div></Card>
        <Card className="p-4 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="kpi-da-pagare"><div className="text-xs text-amber-700 flex items-center gap-1.5"><Wallet size={14} /> Da pagare al partner</div><div className="mt-1 font-display text-xl font-bold text-amber-800">{summary?.da_pagare || 0}</div></Card>
        <Card className="p-4 border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="kpi-importo-pagato"><div className="text-xs text-slate-600 flex items-center gap-1.5"><Wallet size={14} /> Totale pagato al partner</div><div className="mt-1 font-display text-xl font-bold text-slate-800">{fmtEur(summary?.importo_pagato || 0)}</div></Card>
      </div>

      {/* Search */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="pagamento-filter">
          {[["tutte", "Tutte"], ["da_pagare", "Da pagare"], ["pagate", "Pagate"]].map(([k, l]) => (
            <button key={k} onClick={() => setPagFilter(k)} data-testid={`pag-filter-${k}`} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${pagFilter === k ? "bg-rose-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <div className="relative w-full sm:w-80">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca fornitore, cliente, tipo, matricola…" data-testid="antincendio-search" className="pr-8" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><X size={15} /></button>}
        </div>
      </div>

      {/* List */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm text-slate-500">{filtered.length} prodotti</div>
        {filtered.length === 0 ? (
          <div className="py-14 text-center text-slate-400" data-testid="antincendio-empty">Nessun prodotto antincendio registrato.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/60">
                  <th className="px-4 py-2.5 font-medium">Tipo / Matricola</th>
                  <th className="px-4 py-2.5 font-medium">Fornitore</th>
                  <th className="px-4 py-2.5 font-medium">Cliente / Ubicazione</th>
                  <th className="px-4 py-2.5 font-medium text-center">Q.tà</th>
                  <th className="px-4 py-2.5 font-medium">Ultima manut.</th>
                  <th className="px-4 py-2.5 font-medium">Scadenza manut.</th>
                  <th className="px-4 py-2.5 font-medium">Pagamento partner</th>
                  <th className="px-4 py-2.5 font-medium text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((it) => {
                  const b = scadenzaBadge(it.prossima_scadenza);
                  return (
                    <tr key={it.id} className="hover:bg-slate-50" data-testid={`antincendio-row-${it.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-800">{it.tipo || "—"}</div>
                        {it.matricola && <div className="text-xs text-slate-400">matr. {it.matricola}</div>}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{it.fornitore || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-600">
                        <div>{it.cliente || "—"}</div>
                        {it.ubicazione && <div className="text-xs text-slate-400">{it.ubicazione}</div>}
                      </td>
                      <td className="px-4 py-2.5 text-center text-slate-600">{it.quantita || 1}</td>
                      <td className="px-4 py-2.5 text-slate-600">{it.ultima_manutenzione ? fmtDate(it.ultima_manutenzione) : "—"}</td>
                      <td className="px-4 py-2.5"><span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${b.cls}`} data-testid={`scadenza-badge-${it.id}`}>{b.label}</span></td>
                      <td className="px-4 py-2.5"><PagamentoBadge it={it} /></td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button size="sm" variant="outline" className="h-8 border-slate-300" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} title="Modifica"><Pencil size={15} /></Button>
                          <Button size="sm" className="h-8 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => remove(it)} data-testid={`delete-${it.id}`} title="Elimina"><Trash2 size={15} /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="antincendio-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{dialog?.id ? "Modifica prodotto antincendio" : "Nuovo prodotto antincendio"}</DialogTitle>
            <DialogDescription>Fornitore, dati del prodotto e scadenza della prossima manutenzione.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              <div>
                <Label className="text-xs text-slate-600 font-medium">Fornitore</Label>
                <Input value={dialog.form.fornitore} onChange={(e) => setF({ fornitore: e.target.value })} placeholder="Es. LC Estintori" data-testid="f-fornitore" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-medium">Tipo prodotto</Label>
                <Input value={dialog.form.tipo} onChange={(e) => setF({ tipo: e.target.value })} placeholder="Es. Estintore polvere 6kg, CO2 5kg, Idrante…" data-testid="f-tipo" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-medium">Matricola / N. serie</Label>
                <Input value={dialog.form.matricola} onChange={(e) => setF({ matricola: e.target.value })} data-testid="f-matricola" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-medium">Quantità</Label>
                <Input type="number" min="1" value={dialog.form.quantita} onChange={(e) => setF({ quantita: e.target.value })} data-testid="f-quantita" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-medium">Cliente / Azienda</Label>
                <Input value={dialog.form.cliente} onChange={(e) => setF({ cliente: e.target.value })} data-testid="f-cliente" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-medium">Ubicazione</Label>
                <Input value={dialog.form.ubicazione} onChange={(e) => setF({ ubicazione: e.target.value })} placeholder="Es. Magazzino, Piano 1…" data-testid="f-ubicazione" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-medium">Data installazione</Label>
                <Input type="date" value={dialog.form.data_installazione} onChange={(e) => setF({ data_installazione: e.target.value })} data-testid="f-installazione" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-medium">Ultima manutenzione</Label>
                <Input type="date" value={dialog.form.ultima_manutenzione} onChange={(e) => setF({ ultima_manutenzione: e.target.value })} data-testid="f-ultima" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-medium">Prossima scadenza manutenzione</Label>
                <Input type="date" value={dialog.form.prossima_scadenza} onChange={(e) => setF({ prossima_scadenza: e.target.value })} data-testid="f-scadenza" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-600 font-medium">Note</Label>
                <Textarea value={dialog.form.note} onChange={(e) => setF({ note: e.target.value })} rows={2} data-testid="f-note" />
              </div>
              <div className="sm:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="pagamento-partner-section">
                <div className="sm:col-span-2 flex items-center gap-2">
                  <Switch checked={!!dialog.form.pagato_partner} onCheckedChange={(v) => setF({ pagato_partner: v })} data-testid="f-pagato-partner" />
                  <Label className="mb-0 text-sm font-medium text-emerald-800">Fornitura pagata al partner</Label>
                </div>
                {dialog.form.pagato_partner && (<>
                  <div><Label className="text-xs text-slate-600 font-medium">Data pagamento</Label><Input type="date" value={dialog.form.data_pagamento_partner} onChange={(e) => setF({ data_pagamento_partner: e.target.value })} data-testid="f-data-pagamento" /></div>
                  <div><Label className="text-xs text-slate-600 font-medium">Importo pagato (€)</Label><Input type="number" step="0.01" min="0" value={dialog.form.importo_pagato_partner} onChange={(e) => setF({ importo_pagato_partner: e.target.value })} data-testid="f-importo-pagato" /></div>
                  <div className="sm:col-span-2"><Label className="text-xs text-slate-600 font-medium">Nota pagamento</Label><Input value={dialog.form.nota_pagamento_partner} onChange={(e) => setF({ nota_pagamento_partner: e.target.value })} placeholder="Es. bonifico n°…" data-testid="f-nota-pagamento" /></div>
                </>)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="cancel-btn">Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="save-btn">
              <CheckCircle2 size={16} className="mr-1.5" /> {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
